// src/features/pharmacy/pharmacySlice.ts

import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { fetchExistPharmacyList } from "@core/api/pharmacyApi"
import { PharmacyCompanyListSerializer, PharmacyType } from "@/types/pharmacy"

interface PharmacyState {
  // 원외탕전 업체 리스트
  herbalMedicineList: PharmacyCompanyListSerializer[]
  herbalMedicineLoading: boolean
  herbalMedicineError: string | null

  // 약침조제 업체 리스트
  acupuncturePreparationList: PharmacyCompanyListSerializer[]
  acupuncturePreparationLoading: boolean
  acupuncturePreparationError: string | null

  // 기타 업체 리스트
  otherList: PharmacyCompanyListSerializer[]
  otherLoading: boolean
  otherError: string | null
}

// 비동기 Thunk 생성 (Redux Toolkit의 내장 중복 방지 활용)
export const fetchExistPharmacyListThunk = createAsyncThunk(
  "pharmacy/fetchExistPharmacyList",
  async (pharmacyType: PharmacyType, { rejectWithValue }) => {
    try {
      console.debug(`${pharmacyType} 데이터 로드 시작`)
      const response = await fetchExistPharmacyList(pharmacyType)
      console.debug(`${pharmacyType} 데이터 로드 완료`)
      return { pharmacyType, data: response }
    } catch (error) {
      console.error(`${pharmacyType} 로드 실패:`, error)
      return rejectWithValue(error)
    }
  }
)

const initialState: PharmacyState = {
  herbalMedicineList: [],
  herbalMedicineLoading: false,
  herbalMedicineError: null,

  acupuncturePreparationList: [],
  acupuncturePreparationLoading: false,
  acupuncturePreparationError: null,

  otherList: [],
  otherLoading: false,
  otherError: null,
}

const pharmacySlice = createSlice({
  name: "pharmacy",
  initialState,
  reducers: {
    clearError: (state) => {
      state.herbalMedicineError = null
      state.acupuncturePreparationError = null
      state.otherError = null
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchExistPharmacyListThunk 처리
      .addCase(fetchExistPharmacyListThunk.pending, (state, action) => {
        const pharmacyType = action.meta.arg
        switch (pharmacyType) {
          case "herbal_medicine":
            state.herbalMedicineLoading = true
            state.herbalMedicineError = null
            break
          case "acupuncture_preparation":
            state.acupuncturePreparationLoading = true
            state.acupuncturePreparationError = null
            break
          case "other":
            state.otherLoading = true
            state.otherError = null
            break
        }
      })
      .addCase(fetchExistPharmacyListThunk.fulfilled, (state, action) => {
        const { pharmacyType, data } = action.payload
        switch (pharmacyType) {
          case "herbal_medicine":
            state.herbalMedicineList = data || []
            state.herbalMedicineLoading = false
            break
          case "acupuncture_preparation":
            state.acupuncturePreparationList = data || []
            state.acupuncturePreparationLoading = false
            break
          case "other":
            state.otherList = data || []
            state.otherLoading = false
            break
        }
      })
      .addCase(fetchExistPharmacyListThunk.rejected, (state, action) => {
        const pharmacyType = action.meta.arg
        const errorMessage =
          action.error.message || "업체 목록을 불러오는데 실패했습니다."

        switch (pharmacyType) {
          case "herbal_medicine":
            state.herbalMedicineLoading = false
            state.herbalMedicineError = errorMessage
            break
          case "acupuncture_preparation":
            state.acupuncturePreparationLoading = false
            state.acupuncturePreparationError = errorMessage
            break
          case "other":
            state.otherLoading = false
            state.otherError = errorMessage
            break
        }
      })
  },
})

export const { clearError } = pharmacySlice.actions

// 선택자
export const selectHerbalMedicineList = (state: any) =>
  state.pharmacy.herbalMedicineList
export const selectHerbalMedicineLoading = (state: any) =>
  state.pharmacy.herbalMedicineLoading
export const selectHerbalMedicineError = (state: any) =>
  state.pharmacy.herbalMedicineError

export const selectAcupuncturePreparationList = (state: any) =>
  state.pharmacy.acupuncturePreparationList
export const selectAcupuncturePreparationLoading = (state: any) =>
  state.pharmacy.acupuncturePreparationLoading
export const selectAcupuncturePreparationError = (state: any) =>
  state.pharmacy.acupuncturePreparationError

export const selectOtherList = (state: any) => state.pharmacy.otherList
export const selectOtherLoading = (state: any) => state.pharmacy.otherLoading
export const selectOtherError = (state: any) => state.pharmacy.otherError

export default pharmacySlice.reducer
